import React, { useState, useEffect, useCallback } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ToastMessage from '../components/ToastMessage';
import { FaEdit, FaTrash } from 'react-icons/fa';

// ================= VALIDATION =================
const SeriesSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    series: Yup.string().required('Series is required'),
    hour: Yup.string().required('Hour is required'),
    minute: Yup.string().required('Minute is required'),
    ampm: Yup.string().required('AM/PM is required'),
});

// ================= HELPERS =================
const convertTo24Hour = (hour, minute, ampm) => {
    let hr = parseInt(hour, 10);
    if (ampm === 'PM' && hr !== 12) hr += 12;
    if (ampm === 'AM' && hr === 12) hr = 0;
    return `${hr.toString().padStart(2, '0')}:${minute}`;
};

function SeriesForm() {
    const [toast, setToast] = useState(null);
    const [seriesList, setSeriesList] = useState([]);
    const [editingSeries, setEditingSeries] = useState(null);
    const [loading, setLoading] = useState(false);

    const apiUrl = `${process.env.REACT_APP_SERVER_URL}/api/admin/series`;

    // ================= FETCH =================
    const fetchSeries = useCallback(async () => {
        try {
            const res = await axios.get(apiUrl);
            setSeriesList(res.data);
        } catch (err) {
            console.error(err);
        }
    }, [apiUrl]);

    useEffect(() => {
        fetchSeries();
    }, [fetchSeries]);

    // ================= DELETE =================
    const handleDelete = async (id) => {
        if (!window.confirm('Delete this series?')) return;

        try {
            await axios.delete(`${apiUrl}/${id}`);
            setToast({ type: 'success', message: 'Series deleted successfully' });
            fetchSeries();
        } catch {
            setToast({ type: 'danger', message: 'Failed to delete series' });
        }
    };

    // ================= EDIT =================
    const handleEdit = (item) => {
        setEditingSeries(item);
    };

    return (
        <div className="container py-5 flex-grow-1">
            {toast && <ToastMessage type={toast.type} message={toast.message} />}

            <div className="card p-4 shadow mb-4">
                <h4 className="mb-3">
                    {editingSeries ? 'Edit Series' : 'Create Series'}
                </h4>

                <Formik
                    enableReinitialize
                    initialValues={{
                        name: editingSeries?.name || '',
                        series: editingSeries?.series || '',
                        hour: '08',
                        minute: '00',
                        ampm: 'AM',
                    }}
                    validationSchema={SeriesSchema}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            setLoading(true);

                            const time = convertTo24Hour(
                                values.hour,
                                values.minute,
                                values.ampm
                            );

                            if (editingSeries) {
                                await axios.put(`${apiUrl}/${editingSeries.id}`, {
                                    name: values.name,
                                    series: values.series,
                                    time,
                                });
                                setToast({ type: 'success', message: 'Series updated successfully' });
                            } else {
                                await axios.post(apiUrl, {
                                    name: values.name,
                                    series: values.series,
                                    time,
                                });
                                setToast({ type: 'success', message: 'Series created successfully' });
                            }

                            resetForm();
                            setEditingSeries(null);
                            fetchSeries();
                        } catch {
                            setToast({ type: 'danger', message: 'Error saving series' });
                        } finally {
                            setLoading(false);
                        }
                    }}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <div className="mb-3">
                                <label>Name</label>
                                <Field name="name" className="form-control" />
                                <ErrorMessage name="name" component="div" className="text-danger" />
                            </div>

                            <div className="mb-3">
                                <label>Series</label>
                                <Field name="series" className="form-control" />
                                <ErrorMessage name="series" component="div" className="text-danger" />
                            </div>

                            <div className="mb-3">
                                <label>Time</label>
                                <div className="d-flex gap-2">
                                    <Field as="select" name="hour" className="form-select">
                                        {[...Array(12)].map((_, i) => {
                                            const h = String(i + 1).padStart(2, '0');
                                            return <option key={h}>{h}</option>;
                                        })}
                                    </Field>

                                    <Field as="select" name="minute" className="form-select">
                                        {['00', '15', '30', '45'].map(m => (
                                            <option key={m}>{m}</option>
                                        ))}
                                    </Field>

                                    <Field as="select" name="ampm" className="form-select">
                                        <option value="AM">AM</option>
                                        <option value="PM">PM</option>
                                    </Field>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-primary w-100"
                                disabled={isSubmitting || loading}
                            >
                                {editingSeries ? 'Update Series' : 'Create Series'}
                            </button>
                        </Form>
                    )}
                </Formik>
            </div>

            <h5>📺 All Series</h5>

            {seriesList.length === 0 ? (
                <p>No series found.</p>
            ) : (
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Series</th>
                            <th>Time</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {seriesList.map((s) => (
                            <tr key={s.id}>
                                <td>{s.name}</td>
                                <td>{s.series}</td>
                                <td>{s.time}</td>
                                <td>
                                    <button
                                        className="btn btn-sm btn-outline-primary me-2"
                                        onClick={() => handleEdit(s)}
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(s.id)}
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default SeriesForm;
